import { useEffect, useRef, useState } from 'react';
import { ChevronDown, Search } from 'lucide-react';
import { isOverlayIndicator } from '../../utils/drawUtils/indicatorTypes';
import { getIndicatorColor } from '../../utils/findicators/colors';

// Popover replacing the react-select multi-select -- grouped Overlays /
// Oscillators, with a live color swatch per row pulled from theme.js so the
// picker previews the exact on-chart color.
const IndicatorPicker = ({ options, active, onChange }) => {
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  const filtered = options.filter((o) => o.toLowerCase().includes(filter.toLowerCase()));
  const overlays = filtered.filter(isOverlayIndicator);
  const oscillators = filtered.filter((o) => !isOverlayIndicator(o));

  const toggle = (label) => {
    onChange(active.includes(label) ? active.filter((l) => l !== label) : [...active, label]);
  };

  const renderGroup = (title, items) =>
    items.length > 0 && (
      <div className="py-1.5">
        <div className="px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-gray-400 dark:text-white/30">{title}</div>
        {items.map((label) => (
          <label
            key={label}
            className="flex items-center gap-2.5 px-3 py-1.5 text-[13px] text-gray-700 hover:bg-gray-100 dark:text-white/70 dark:hover:bg-white/5 cursor-pointer"
          >
            <input
              type="checkbox"
              checked={active.includes(label)}
              onChange={() => toggle(label)}
              className="accent-indigo-500"
            />
            <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: getIndicatorColor(label) }} />
            <span>{label.replace(/-/g, ' ')}</span>
          </label>
        ))}
      </div>
    );

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1.5 px-3 py-1.5 text-[12px] font-medium rounded-lg bg-gray-100 dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.06] text-gray-700 dark:text-white/70 hover:bg-gray-200 dark:hover:bg-white/[0.06] transition-colors"
      >
        Indicators{active.length > 0 ? ` (${active.length})` : ''}
        <ChevronDown size={13} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute z-30 mt-1.5 w-64 max-h-96 overflow-y-auto rounded-xl border border-gray-200 dark:border-white/10 bg-white dark:bg-[#0d0d16] shadow-2xl shadow-gray-400/20 dark:shadow-black/50">
          <div className="p-2 border-b border-gray-200 dark:border-white/5">
            <div className="flex items-center gap-1.5 px-2 py-1 rounded-lg bg-gray-100 dark:bg-white/[0.04]">
              <Search size={12} className="text-gray-400 dark:text-white/30" />
              <input
                autoFocus
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                placeholder="Search indicators..."
                className="flex-1 bg-transparent text-[12px] text-gray-800 dark:text-white/80 placeholder-gray-400 dark:placeholder-white/25 outline-none"
              />
            </div>
          </div>
          {renderGroup('Overlays', overlays)}
          {renderGroup('Oscillators', oscillators)}
          {filtered.length === 0 && <div className="px-3 py-4 text-center text-[12px] text-gray-400 dark:text-white/30">No matches</div>}
        </div>
      )}
    </div>
  );
};

export default IndicatorPicker;
